import React from "react";
import MenuItem from "@mui/material/MenuItem";
import { AppButton, AppTextField } from "./ui";
import "./SystemCreditsEditor.scss";

const BODY_BLOCK_DEFAULT = { type: "paragraph", title: "", text: "" };
const ATTRIBUTION_DEFAULT = {
  label: "",
  creator: "",
  sourceTitle: "",
  sourceUrl: "",
  licenseName: "",
  licenseUrl: "",
  notes: "",
};
const LICENSED_ASSET_DEFAULT = {
  title: "",
  description: "",
  sourceTitle: "",
  sourceCreator: "",
  sourceUrl: "",
  sourceLicenseName: "",
  sourceLicenseUrl: "",
  adaptationLicenseName: "",
  adaptationLicenseUrl: "",
  changesMade: "",
  modelViewer: { src: "", poster: "", alt: "" },
  downloads: [],
};
const DOWNLOAD_DEFAULT = {
  label: "",
  url: "",
  format: "",
  licenseName: "",
};
const FUNDING_DEFAULT = {
  funder: "",
  program: "",
  grantNumber: "",
  statement: "",
  url: "",
};

const COMMON_LICENSES = [
  ["CC0 1.0", "https://creativecommons.org/publicdomain/zero/1.0/"],
  ["CC BY 4.0", "https://creativecommons.org/licenses/by/4.0/"],
  ["CC BY-SA 4.0", "https://creativecommons.org/licenses/by-sa/4.0/"],
  ["CC BY-NC 4.0", "https://creativecommons.org/licenses/by-nc/4.0/"],
  ["CC BY-NC-SA 4.0", "https://creativecommons.org/licenses/by-nc-sa/4.0/"],
  ["CC BY-ND 4.0", "https://creativecommons.org/licenses/by-nd/4.0/"],
  ["CC BY-NC-ND 4.0", "https://creativecommons.org/licenses/by-nc-nd/4.0/"],
  [
    "Public Domain Mark 1.0",
    "https://creativecommons.org/publicdomain/mark/1.0/",
  ],
  ["All rights reserved", ""],
];

const COMMON_LICENSE_URLS = Object.fromEntries(COMMON_LICENSES);
const COMMON_LICENSE_NAMES = new Set(COMMON_LICENSES.map(([name]) => name));

const cloneDefault = (value) => JSON.parse(JSON.stringify(value));

const ItemActions = ({ label, index, count, onMove, onRemove }) => (
  <div className="creditsItemActions">
    <AppButton
      kind="ghost"
      aria-label={`Move ${label} ${index + 1} up`}
      disabled={index === 0}
      onClick={() => onMove(index, index - 1)}
    >
      Move up
    </AppButton>
    <AppButton
      kind="ghost"
      aria-label={`Move ${label} ${index + 1} down`}
      disabled={index === count - 1}
      onClick={() => onMove(index, index + 1)}
    >
      Move down
    </AppButton>
    <AppButton
      kind="danger"
      aria-label={`Remove ${label} ${index + 1}`}
      onClick={() => onRemove(index)}
    >
      Remove
    </AppButton>
  </div>
);

const CreditsSection = ({ title, description, addLabel, onAdd, children }) => (
  <section className="creditsSection">
    <div className="creditsSectionHeader">
      <div>
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
      <AppButton kind="secondary" onClick={onAdd}>
        {addLabel}
      </AppButton>
    </div>
    {children}
  </section>
);

const SystemCreditsEditor = ({
  systemName,
  systemDescription,
  value,
  onChange,
  errors = {},
  warnings = [],
}) => {
  const getError = (path) => errors[path] || "";

  const fieldProps = (path) => ({
    error: Boolean(getError(path)),
    helperText: getError(path) || undefined,
  });

  const setCollection = (collection, nextItems) => {
    onChange({ ...value, [collection]: nextItems });
  };

  const addItem = (collection, defaultValue) => {
    setCollection(collection, [
      ...(value[collection] || []),
      cloneDefault(defaultValue),
    ]);
  };

  const updateItem = (collection, index, patch) => {
    setCollection(
      collection,
      value[collection].map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...patch } : item
      )
    );
  };

  const removeItem = (collection, index) => {
    setCollection(
      collection,
      value[collection].filter((_, itemIndex) => itemIndex !== index)
    );
  };

  const moveItem = (collection, fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= value[collection].length) return;
    const nextItems = [...value[collection]];
    const [movedItem] = nextItems.splice(fromIndex, 1);
    nextItems.splice(toIndex, 0, movedItem);
    setCollection(collection, nextItems);
  };

  const updateDownload = (assetIndex, downloadIndex, patch) => {
    const asset = value.licensedAssets[assetIndex];
    const downloads = (asset.downloads || []).map((download, index) =>
      index === downloadIndex ? { ...download, ...patch } : download
    );
    updateItem("licensedAssets", assetIndex, { downloads });
  };

  const addDownload = (assetIndex) => {
    const asset = value.licensedAssets[assetIndex];
    updateItem("licensedAssets", assetIndex, {
      downloads: [...(asset.downloads || []), cloneDefault(DOWNLOAD_DEFAULT)],
    });
  };

  const removeDownload = (assetIndex, downloadIndex) => {
    const asset = value.licensedAssets[assetIndex];
    updateItem("licensedAssets", assetIndex, {
      downloads: (asset.downloads || []).filter(
        (_, index) => index !== downloadIndex
      ),
    });
  };

  const moveDownload = (assetIndex, fromIndex, toIndex) => {
    const asset = value.licensedAssets[assetIndex];
    const downloads = [...(asset.downloads || [])];
    if (toIndex < 0 || toIndex >= downloads.length) return;
    const [movedDownload] = downloads.splice(fromIndex, 1);
    downloads.splice(toIndex, 0, movedDownload);
    updateItem("licensedAssets", assetIndex, { downloads });
  };

  return (
    <div className="systemCreditsEditor">
      <div className="creditsContext" aria-label="System context">
        <span className="creditsContextLabel">System</span>
        <strong>{systemName || "Unnamed system"}</strong>
        <p>{systemDescription || "No system description"}</p>
      </div>

      {warnings.length > 0 && (
        <div className="creditsWarnings" role="status">
          <strong>Review before publishing</strong>
          <ul>
            {warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      )}

      <section className="creditsSection">
        <div className="creditsSectionHeader">
          <div>
            <h3>About box</h3>
            <p>
              Short experience summary shown in the viewer. Full credits prose
              belongs in body blocks.
            </p>
          </div>
        </div>
        <div className="creditsItem">
          <AppTextField
            label="About text"
            inputProps={{ "aria-label": "About text" }}
            value={value.aboutText || ""}
            onChange={(event) =>
              onChange({ ...value, aboutText: event.target.value })
            }
            multiline
            minRows={3}
            fullWidth
            {...fieldProps("aboutText")}
          />
        </div>
      </section>

      <CreditsSection
        title="Body blocks"
        description="Custom prose shown on the public credits page."
        addLabel="Add body block"
        onAdd={() => addItem("bodyBlocks", BODY_BLOCK_DEFAULT)}
      >
        {value.bodyBlocks.map((block, index) => {
          const contentField = block.type === "markdown" ? "markdown" : "text";
          return (
            <div className="creditsItem" key={`body-block-${index}`}>
              <div className="creditsItemHeading">
                <h4>Body block {index + 1}</h4>
                <ItemActions
                  label="body block"
                  index={index}
                  count={value.bodyBlocks.length}
                  onMove={(from, to) => moveItem("bodyBlocks", from, to)}
                  onRemove={(itemIndex) => removeItem("bodyBlocks", itemIndex)}
                />
              </div>
              <div className="creditsFieldGrid creditsFieldGrid--three">
                <AppTextField
                  select
                  label="Type"
                  inputProps={{
                    "aria-label": `Body block ${index + 1} type`,
                  }}
                  value={block.type || "paragraph"}
                  onChange={(event) =>
                    updateItem("bodyBlocks", index, { type: event.target.value })
                  }
                  fullWidth
                >
                  <MenuItem value="paragraph">Paragraph</MenuItem>
                  <MenuItem value="heading">Heading</MenuItem>
                  <MenuItem value="markdown">Markdown</MenuItem>
                  <MenuItem value="callout">Callout</MenuItem>
                </AppTextField>
                <AppTextField
                  label="Title (optional)"
                  inputProps={{
                    "aria-label": `Body block ${index + 1} title (optional)`,
                  }}
                  value={block.title || ""}
                  onChange={(event) =>
                    updateItem("bodyBlocks", index, { title: event.target.value })
                  }
                  fullWidth
                />
                <AppTextField
                  className="creditsFieldSpan"
                  label="Content"
                  inputProps={{
                    "aria-label": `Body block ${index + 1} content`,
                  }}
                  value={block[contentField] || ""}
                  onChange={(event) =>
                    updateItem("bodyBlocks", index, {
                      [contentField]: event.target.value,
                    })
                  }
                  multiline
                  minRows={3}
                  fullWidth
                />
              </div>
            </div>
          );
        })}
      </CreditsSection>

      <CreditsSection
        title="Attributions"
        description="General source credits and references."
        addLabel="Add attribution"
        onAdd={() => addItem("attributions", ATTRIBUTION_DEFAULT)}
      >
        {value.attributions.map((attribution, index) => (
          <div className="creditsItem" key={`attribution-${index}`}>
            <div className="creditsItemHeading">
              <h4>Attribution {index + 1}</h4>
              <ItemActions
                label="attribution"
                index={index}
                count={value.attributions.length}
                onMove={(from, to) => moveItem("attributions", from, to)}
                onRemove={(itemIndex) => removeItem("attributions", itemIndex)}
              />
            </div>
            <div className="creditsFieldGrid">
              {[
                ["label", "Label"],
                ["creator", "Creator"],
                ["sourceUrl", "Source URL"],
              ].map(([field, label]) => (
                <AppTextField
                  key={field}
                  label={label}
                  inputProps={{
                    "aria-label": `Attribution ${index + 1} ${label}`,
                  }}
                  value={attribution[field] || ""}
                  onChange={(event) =>
                    updateItem("attributions", index, {
                      [field]: event.target.value,
                    })
                  }
                  fullWidth
                  {...fieldProps(`attributions.${index}.${field}`)}
                />
              ))}
            </div>
          </div>
        ))}
      </CreditsSection>

      <CreditsSection
        title="Licensed assets"
        description="Source, adaptation, preview, and download licensing details."
        addLabel="Add licensed asset"
        onAdd={() => addItem("licensedAssets", LICENSED_ASSET_DEFAULT)}
      >
        {value.licensedAssets.map((asset, index) => (
          <div className="creditsItem" key={`licensed-asset-${index}`}>
            <div className="creditsItemHeading">
              <h4>Licensed asset {index + 1}</h4>
              <ItemActions
                label="licensed asset"
                index={index}
                count={value.licensedAssets.length}
                onMove={(from, to) => moveItem("licensedAssets", from, to)}
                onRemove={(itemIndex) => removeItem("licensedAssets", itemIndex)}
              />
            </div>
            <div className="creditsFieldGrid">
              {[
                ["title", "Title"],
                ["sourceUrl", "Source URL"],
              ].map(([field, label]) => (
                <AppTextField
                  key={field}
                  label={label}
                  inputProps={{
                    "aria-label": `Licensed asset ${index + 1} ${label}`,
                  }}
                  value={asset[field] || ""}
                  onChange={(event) =>
                    updateItem("licensedAssets", index, {
                      [field]: event.target.value,
                    })
                  }
                  fullWidth
                  {...fieldProps(`licensedAssets.${index}.${field}`)}
                />
              ))}
              <AppTextField
                select
                label="License"
                inputProps={{
                  "aria-label": `Licensed asset ${index + 1} Source license name`,
                }}
                value={asset.sourceLicenseName || ""}
                onChange={(event) => {
                  const sourceLicenseName = event.target.value;
                  updateItem("licensedAssets", index, {
                    sourceLicenseName,
                    sourceLicenseUrl:
                      COMMON_LICENSE_URLS[sourceLicenseName] ??
                      asset.sourceLicenseUrl ??
                      "",
                  });
                }}
                fullWidth
                {...fieldProps(`licensedAssets.${index}.sourceLicenseName`)}
              >
                <MenuItem value="" disabled>
                  Select a license
                </MenuItem>
                {!COMMON_LICENSE_NAMES.has(asset.sourceLicenseName) &&
                  asset.sourceLicenseName && (
                    <MenuItem value={asset.sourceLicenseName}>
                      {asset.sourceLicenseName} (custom)
                    </MenuItem>
                  )}
                {COMMON_LICENSES.map(([name]) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </AppTextField>
              <AppTextField
                className="creditsFieldSpan"
                label="Changes made"
                inputProps={{
                  "aria-label": `Licensed asset ${index + 1} changes made`,
                }}
                value={asset.changesMade || ""}
                onChange={(event) =>
                  updateItem("licensedAssets", index, {
                    changesMade: event.target.value,
                  })
                }
                multiline
                minRows={2}
                fullWidth
                {...fieldProps(`licensedAssets.${index}.changesMade`)}
              />
            </div>

            <h5>Model preview</h5>
            <AppTextField
              label="Model URL"
              inputProps={{
                "aria-label": `Licensed asset ${index + 1} Model URL`,
              }}
              value={asset.modelViewer?.src || ""}
              onChange={(event) =>
                updateItem("licensedAssets", index, {
                  modelViewer: {
                    ...(asset.modelViewer || {}),
                    src: event.target.value,
                  },
                })
              }
              fullWidth
              {...fieldProps(`licensedAssets.${index}.modelViewer.src`)}
            />

            <div className="creditsSubsectionHeading">
              <h5>Downloads</h5>
              <AppButton kind="secondary" onClick={() => addDownload(index)}>
                Add download
              </AppButton>
            </div>
            {(asset.downloads || []).map((download, downloadIndex) => (
              <div
                className="creditsDownload"
                key={`licensed-asset-${index}-download-${downloadIndex}`}
              >
                <div className="creditsItemHeading">
                  <h6>Download {downloadIndex + 1}</h6>
                  <ItemActions
                    label={`licensed asset ${index + 1} download`}
                    index={downloadIndex}
                    count={asset.downloads.length}
                    onMove={(from, to) => moveDownload(index, from, to)}
                    onRemove={(itemIndex) => removeDownload(index, itemIndex)}
                  />
                </div>
                <div className="creditsFieldGrid creditsFieldGrid--four">
                  {[
                    ["label", "Label"],
                    ["url", "URL"],
                  ].map(([field, label]) => (
                    <AppTextField
                      key={field}
                      label={label}
                      inputProps={{
                        "aria-label": `Asset ${index + 1} download ${downloadIndex + 1} ${label}`,
                      }}
                      value={download[field] || ""}
                      onChange={(event) =>
                        updateDownload(index, downloadIndex, {
                          [field]: event.target.value,
                        })
                      }
                      fullWidth
                      {...fieldProps(
                        `licensedAssets.${index}.downloads.${downloadIndex}.${field}`
                      )}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </CreditsSection>

      <CreditsSection
        title="Funding"
        description="Funders, programs, grants, and required statements."
        addLabel="Add funding credit"
        onAdd={() => addItem("funding", FUNDING_DEFAULT)}
      >
        {value.funding.map((credit, index) => (
          <div className="creditsItem" key={`funding-${index}`}>
            <div className="creditsItemHeading">
              <h4>Funding credit {index + 1}</h4>
              <ItemActions
                label="funding credit"
                index={index}
                count={value.funding.length}
                onMove={(from, to) => moveItem("funding", from, to)}
                onRemove={(itemIndex) => removeItem("funding", itemIndex)}
              />
            </div>
            <div className="creditsFieldGrid">
              {[
                ["funder", "Funder"],
                ["grantNumber", "Grant number"],
              ].map(([field, label]) => (
                <AppTextField
                  key={field}
                  label={label}
                  inputProps={{
                    "aria-label": `Funding credit ${index + 1} ${label}`,
                  }}
                  value={credit[field] || ""}
                  onChange={(event) =>
                    updateItem("funding", index, { [field]: event.target.value })
                  }
                  fullWidth
                  {...fieldProps(`funding.${index}.${field}`)}
                />
              ))}
              <AppTextField
                className="creditsFieldSpan"
                label="Statement"
                inputProps={{
                  "aria-label": `Funding credit ${index + 1} statement`,
                }}
                value={credit.statement || ""}
                onChange={(event) =>
                  updateItem("funding", index, { statement: event.target.value })
                }
                multiline
                minRows={2}
                fullWidth
              />
            </div>
          </div>
        ))}
      </CreditsSection>
    </div>
  );
};

export default SystemCreditsEditor;
